import { isNumber } from "lodash"
import { isAgentLogin } from "./auth"
import { Toast as toast} from "components"
import strings from "strings"
import moment from "moment"

const t = strings.TABLE_DEFAULTS

const getCarrierParamsData = (formData: any) => {
  const paramData: any = {}

  for (const key in formData) {
    if (formData[key] !== undefined && key !== 'carrierName:fuzzy') {
      paramData['codeType'] = key?.toUpperCase()
      paramData['codeValue'] = formData[key]
    } else if (formData[key] !== undefined && key === 'carrierName:fuzzy') {
      paramData[key] = formData[key]
    }
  }

  return paramData
}

const getChildCarrierParamsData = (formData: any) => {
  const paramData: any = {}

  for (const key in formData) {
    if (formData[key] !== undefined ) {
      paramData[key] = formData[key]
    }
  }

  return paramData
}

const getCodeTypeValue = (listData: any) => {
  const paramData: any = {}
  listData.forEach((item: any) => {
    if (item.codeType === 'DOT') {
      paramData['codeType'] = item.codeType
      paramData['codeValue'] = item.codeValue
    } else if (item.codeType === 'UUID') {
      paramData['codeType'] = item.codeType
      paramData['codeValue'] = item.codeValue
    }
  })
  return paramData
}

// Function to check if all keys are present and have non-empty values
function areKeysValid(obj: any, keys: any[]) {
  for (const key of keys) {
    if (!(key in obj) || !obj[key] || obj[key].includes('�')) {
      return false
    }
  }
  return true
}

const getPercentageDiff = (currentValue: number, previousValue: number, totalValue: number): number => {
  if (totalValue === 0) return 0

  const previousPercentage = (previousValue / totalValue) * 100
  const currentPercentage = (currentValue / totalValue) * 100
  const diff = currentPercentage - previousPercentage

  return diff
}

const generateVariationTooltip = (currentValue: any, previousValue: any, totalValue: number, withPercentages: boolean): string => {
  if ((currentValue === undefined && previousValue === undefined)
    || (currentValue === null && previousValue === null) 
    || (isNaN(currentValue) && isNaN(previousValue))) {
    return 'No data'
  }
  if (currentValue === undefined || currentValue === null || isNaN(currentValue)) {
    return 'No data'
  }
  if (previousValue === undefined || previousValue === null || isNaN(previousValue)) {
    if (withPercentages === true && !isNaN(currentValue))
      return `${currentValue.toFixed(2)} (100%)`
    else
      return `${currentValue.toFixed(2)}`
  }

  if (withPercentages === true && !isNaN(totalValue) && !isNaN(previousValue) && !isNaN(currentValue)) {
    const previousPercentage = (previousValue / totalValue) * 100
    const currentPercentage = (currentValue / totalValue) * 100

    if (!isNaN(previousPercentage) && !isNaN(currentPercentage)) {
      return `${previousValue.toFixed(2)} (${previousPercentage.toFixed(2)}%) -> ${currentValue.toFixed(2)} (${currentPercentage.toFixed(2)}%)`
    } else {
      return `${previousValue.toFixed(2)} -> ${currentValue.toFixed(2)}`
    }
  }
  else {
    return `${previousValue.toFixed(2)} -> ${currentValue.toFixed(2)}`
  }
}

const formatRiskCell = (value: any, decimals?: number) => {
  const formatValue = (num: number, decimals?: number) => {
    if (isNaN(num)) return t.DASH_VALUE;
    const formatted = decimals ? Math.abs(num).toFixed(decimals) : Math.trunc(Math.abs(num));
    return formatted?.toLocaleString() || t.DASH_VALUE
  }
  const isEmptyString = (str: any): boolean => typeof str === 'string' && str.trim() === ''

  if (isAgentLogin()) {
    if (isNumber(value) || isNumber(parseFloat(value))) {
      return formatValue(value, decimals)
    }
    if (value === null || value === undefined || isEmptyString(value)) {
      return t.DASH_VALUE
    }
    return String(value)
  }
  else {
    return formatValue(value, decimals)
  }
}

const ratingColor = (value: any) => {
  if (typeof value !== 'string') {
    return '';
  }
  switch (value?.toLocaleLowerCase()) {
    case 'poor':
      return '#EA1C04';
    case 'average':
      return '#4276C9';
    case 'great':
      return '#34864E';
    default:
      return '';
  }
};

const copyToClipboard = (value: string, message?: string) => {
  navigator.clipboard.writeText(value);
  toast({
    id: value,
    type: "info",
    title: `Copied to clipboard`,
    subTitle: message ? message : value,
    autoClose: 2000
  })
}

const isArrayEmpty = (arr: any) : boolean => {
  return !arr || arr.length === 0
}

const formatEventTypeLabel = (item: any) => {
  return item
    ?.toLowerCase()
    ?.split('_')
    ?.map((word: string) => word.charAt(0).toUpperCase() + word.slice(1))
    ?.join(' ')
}

const getEventIcon = (eventType: string, useTwoTone?: boolean): string => {
  if (useTwoTone) {
    const eventIconMapTwoTone = new Map<string, string>([
      ['HARSH_BRAKING', 'SpeedTwoTone'],
      ['TAILGATING', 'WarningAmberTwoTone'],
      ['HARSH_CORNER', 'TurnLeftTwoTone'],
      ['HARSH_ACCELERATION', 'ArrowUpwardTwoTone'],
      ['LANE_DEPATURE', 'DirectionsCarTwoTone'],
      ['ROUGH_UNEVEN_SURFACE', 'RemoveRoadTwoTone'],
      ['OTHER', 'ReportProblemTwoTone'],
      ['ROLL_STABILITY', 'SyncProblemTwoTone'],
      ['SIGN_VIOLATION_OR_ROLLING', 'TrafficTwoTone'],
      ['PARKED_ON_HIGHWAY_RAMP', 'WrongLocationTwoTone'],
      ['CRITICAL_TAILGATING', 'WarningAmberTwoTone'],
      ['NEAR_COLLISION', 'DirectionsCarTwoTone'],
      ['CRASH', 'CarCrashTwoTone'],
      ['POSSIBLE_ACCIDENT', 'CarCrashTwoTone'],
      ['SHOCK', 'VibrationTwoTone'],
      ['BLIND_SPOT', 'VisibilityOffTwoTone'],
      ['IDLING', 'TimerTwoTone'],
      ['UNAUTHORIZED_DRIVER', 'PersonOffTwoTone'],
      ['LANE_VIOLATION', 'SwapHorizTwoTone'],
      ['BACKING', 'ArrowDownwardTwoTone'],
      ['CABIN_OCCUPANCY', 'EventSeatTwoTone'],
      ['THEFT_ALARM', 'NotificationsActiveTwoTone'],
      ['DVIR_DEFECT', 'FactCheckTwoTone'],
      ['HOS_VIOLATION', 'ScheduleTwoTone'],
      ['OPERATING_HOURS_VIOLATION', 'AvTimerTwoTone'],
      ['DRIVING_SCORE', 'AssessmentTwoTone'],
      ['CARGO_VIOLATION', 'InventoryTwoTone'],
      ['PEDESTRIAN', 'DirectionsWalkTwoTone'],
      ['FACE_MASK_CHECK', 'MasksTwoTone'],
      ['AUX_INPUT', 'SettingsInputComponentTwoTone'],
      ['DRIVER_ATTENDANCE', 'EventAvailableTwoTone'],
      ['IGNITION', 'KeyTwoTone'],
      ['TRIP_STATE', 'RouteTwoTone'],
      ['DEVICE_FAULT', 'DeviceUnknownTwoTone'],
      ['DEVICE_CONNECTIVITY', 'WifiOffTwoTone'],
      ['DEVICE_POWER', 'PowerTwoTone'],
      ['LOW_BATTERY', 'BatteryAlertTwoTone'],
      ['FUEL_EVENT', 'LocalGasStationTwoTone'],
      ['MAINTENANCE', 'BuildTwoTone'],
      ['VIDEO_OPERATION', 'VideocamTwoTone'],
      ['NOT_SPECIFIED_EVENT', 'HelpOutlineTwoTone'],
      ['UNKNOWN_EVENT', 'QuestionMarkTwoTone'],
    ])
    return eventIconMapTwoTone.get(eventType) ?? 'ReportProblemTwoTone'
  }

  const eventIconMap = new Map<string, string>([
    ['HARSH_BRAKING', 'Speed'],
    ['TAILGATING', 'WarningAmber'],
    ['HARSH_CORNER', 'TurnLeft'],
    ['HARSH_ACCELERATION', 'ArrowUpward'],
    ['LANE_DEPATURE', 'DirectionsCar'],
    ['ROUGH_UNEVEN_SURFACE', 'RemoveRoad'],
    ['OTHER', 'ReportProblem'],
    ['ROLL_STABILITY', 'SyncProblem'],
    ['SIGN_VIOLATION_OR_ROLLING', 'Traffic'],
    ['PARKED_ON_HIGHWAY_RAMP', 'WrongLocation'],
    ['CRITICAL_TAILGATING', 'WarningAmber'],
    ['NEAR_COLLISION', 'DirectionsCar'],
    ['CRASH', 'CarCrash'],
    ['POSSIBLE_ACCIDENT', 'CarCrash'],
    ['SHOCK', 'Vibration'],
    ['BLIND_SPOT', 'VisibilityOff'],
    ['IDLING', 'Timer'],
    ['UNAUTHORIZED_DRIVER', 'PersonOff'],
    ['LANE_VIOLATION', 'SwapHoriz'],
    ['BACKING', 'ArrowDownward'],
    ['CABIN_OCCUPANCY', 'EventSeat'],
    ['THEFT_ALARM', 'NotificationsActive'],
    ['DVIR_DEFECT', 'FactCheck'],
    ['HOS_VIOLATION', 'Schedule'],
    ['OPERATING_HOURS_VIOLATION', 'AvTimer'],
    ['DRIVING_SCORE', 'Assessment'],
    ['CARGO_VIOLATION', 'Inventory'],
    ['PEDESTRIAN', 'DirectionsWalk'],
    ['FACE_MASK_CHECK', 'Masks'],
    ['AUX_INPUT', 'SettingsInputComponent'],
    ['DRIVER_ATTENDANCE', 'EventAvailable'],
    ['IGNITION', 'Key'],
    ['TRIP_STATE', 'Route'],
    ['DEVICE_FAULT', 'DeviceUnknown'],
    ['DEVICE_CONNECTIVITY', 'WifiOff'],
    ['DEVICE_POWER', 'Power'],
    ['LOW_BATTERY', 'BatteryAlert'],
    ['FUEL_EVENT', 'LocalGasStation'],
    ['MAINTENANCE', 'Build'],
    ['VIDEO_OPERATION', 'Videocam'],
    ['NOT_SPECIFIED_EVENT', 'HelpOutline'],
    ['UNKNOWN_EVENT', 'QuestionMark'],
  ])
  return eventIconMap.get(eventType) ?? 'ReportProblem'
}

const formatDate = (dateString: string, includeTime: boolean = true) => {
  if (!dateString) return null
  const date = new Date(dateString)
  return includeTime ? date.toLocaleString() : date.toLocaleDateString()
}

const formatDateMMDDYYYY = (dateString: string) => {
  if (!dateString) return null
  return moment(dateString).format('MM/DD/YYYY')
}

const formatDateToUTC = (momentDate: moment.Moment) => {
  return momentDate.utc().format('YYYY-MM-DD[T]HH:mm:ss')
}

const formatDateYYYYMMDD = (momentDate: moment.Moment) => {
  return momentDate.format('YYYY-MM-DD')
}


const getCodeTypesAndCodeValues = (selectedCarrierCodes: any) => {
  if (!selectedCarrierCodes) return null

  const priorityMappings = [
    { key: 'dot', codeType: 'DOT' },
    { key: 'mc', codeType: 'MC' },
    { key: 'scac', codeType: 'SCAC' },
    { 
      key: 'cuid', 
      codeType: selectedCarrierCodes.cuidName !== '-' ? selectedCarrierCodes.cuidName : null 
    },
  ]

  for (const { key, codeType } of priorityMappings) {
    if (codeType && selectedCarrierCodes[key]) {
      return { codeType, codeValue: selectedCarrierCodes[key] }
    }
  }

  return null
}

const getEditCarrierCodeTypeAndValue = (
  initialFormValues: any,
  emptyValue: string
) => {
  if (!initialFormValues) return { codeType: '', codeValue: '' }

  const priorityMappings = [
    { key: 'carrierId', codeType: 'TCID' },
    { key: 'dot', codeType: 'DOT' },
    { key: 'mc', codeType: 'MC' },
    { key: 'scac', codeType: 'SCAC' },
    { key: 'cuid', codeType: 'CUID' },
  ]

  for (const { key, codeType } of priorityMappings) {
    const value = initialFormValues[key]
    if (value && value !== emptyValue) {
      return { codeType, codeValue: String(value) }
    }
  }

  return { codeType: '', codeValue: '' }
}

const generateInvitationUrl = (token: string) => {
  const currentUrl = window.location.href
  const isLocalhost = currentUrl.includes('localhost')
  const baseUrl = isLocalhost
    ? 'http://localhost:3000'
    : `https://${window.location.hostname}`

  return `${baseUrl}/cob?invitationkey=${token}`
}

const exportTableAsCSV = (csvString: string, fileName: string) => {
  const blob = new Blob([csvString], { type: 'text/csv' })
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = fileName
  a.click()
  URL.revokeObjectURL(url)
}

const getCredentialStatuses = () => [
  { status: "PAUSED" },
  { status: "DISCONNECTED" },
  { status: "ACTIVE" }
]

const isShortcodeExcludedForDriversDashboardAccess = () => {
  const excludedShortcodes = (
    process.env.REACT_APP_EXCLUDED_SHORTCODES_FOR_DRIVERS_DASHBOARD?.split(',') || []
  ).filter((s) => s.trim().length > 0)

  if (excludedShortcodes.length === 0) return false

  const hostname = window.location.hostname

  const hasExcludedShortcode = excludedShortcodes.some((shortcode) =>
    hostname.includes(shortcode)
  )
  return hasExcludedShortcode
}

const isShortcodeEnabledForUnderwritersScoreDashboard = () => {
  const enabledShortcodes = (
    process.env.REACT_APP_UNDERWRITERS_SCORE_ACCESS_SHORTCODES?.split(',') || []
  )
    .map((s) => s.trim())
    .filter(Boolean)

  if (enabledShortcodes.length === 0) return true

  const hostname = window.location.hostname

  const hasEnabledShortcode = enabledShortcodes.some((shortcode) =>
    hostname.includes(shortcode)
  )
  return hasEnabledShortcode
}

const isShortcodeEnabledForNBFleetDashboard = () => {
  const enabledShortcodes = (
    process.env.REACT_APP_NB_FLEET_ACCESS_SHORTCODES?.split(',') || []
  )
    .map((s) => s.trim())
    .filter(Boolean)

  if (enabledShortcodes.length === 0) return true

  const hostname = window.location.hostname

  const hasEnabledShortcode = enabledShortcodes.some((shortcode) =>
    hostname.includes(shortcode)
  )
  return hasEnabledShortcode
}

const isShortcodeEnabledForNBWidgetFrenchStrings = () => {
  const enabledShortcodes = (
    process.env.REACT_APP_NB_WIDGET_FRENCH_STRINGS?.split(',') || []
  )
    .map((s) => s.trim())
    .filter(Boolean)

  if (enabledShortcodes.length === 0) return false

  const hostname = window.location.hostname

  const hasEnabledShortcode = enabledShortcodes.some((shortcode) =>
    hostname.includes(shortcode)
  )
  return hasEnabledShortcode
}

const isShortcodeExcludedForMileageLocationReporting = () => {
  const excludedShortcodes = (
    process.env.REACT_APP_EXCLUDED_SHORTCODES_FOR_MILEAGE_LOCATION_REPORTING?.split(',') || []
  ).filter((s) => s.trim().length > 0)

  if (excludedShortcodes.length === 0) return false

  const hostname = window.location.hostname

  const hasExcludedShortcode = excludedShortcodes.some((shortcode) =>
    hostname.includes(shortcode)
  )
  return hasExcludedShortcode
}

const abbreviateThousandsNumber = (number: number) => {
  if (number >= 1000) {
    return Math.round(number / 1000) + 'K';
  }
  return number.toString()
}

const formatCompactNumber = (num: number | null | undefined): string => {
  if (num === null || num === undefined) {
    return '0'
  }

  const lookup = [
    { value: 1, symbol: '' },
    { value: 1e3, symbol: 'K' },
    { value: 1e6, symbol: 'M' },
    { value: 1e9, symbol: 'B' },
  ];

  const item = lookup
    .slice()
    .reverse()
    .find((item) => num >= item.value)
  if (item) {
    const value = (num / item.value).toFixed(2)
    return parseFloat(value).toString() + item.symbol
  }
  return num.toString()
}

const getHostInfo = () => {
  const hostname = window.location.hostname;
  const parts = hostname.split('.');
  
  return {
    fullHostname: hostname,
    subdomain: parts.length > 2 ? parts[0] : null,
    domain: parts.length > 2 ? parts.slice(1).join('.') : hostname,
    isLocalhost: hostname === 'localhost' || hostname === '127.0.0.1',
    parts: parts
  };
};

const isObjectEmpty = (obj: any) => {
  return !obj || Object.keys(obj).length === 0
}

const formatNumber = (num: number | undefined): string => {
  if (num === undefined || num === null) return '-'
  return num.toLocaleString('en-US')
}

const formatKm = (num: number | undefined, kmSuffix: string = 'km'): string => {
  if (num === undefined || num === null) return '-'
  return `${Math.round(num).toLocaleString('en-US')} ${kmSuffix}`
}

const timeAgo = (iso: string | null): string => {
  if (!iso) return '-'
  const then = moment(iso)
  if (!then.isValid()) return '-'
  const diffMinutes = moment().diff(then, 'minutes')
  if (diffMinutes < 60) return `${Math.max(diffMinutes, 0)}m ago`
  const diffHours = moment().diff(then, 'hours')
  if (diffHours < 24) return `${diffHours}h ago`
  return formatDate(iso, false) || '-'
}

const formatDateTimeNow = (): string => {
  const now = new Date()
  return now.toLocaleString('en-US', {
    month: '2-digit',
    day: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: true
  })
}

export { 
  getCarrierParamsData, 
  getChildCarrierParamsData, 
  getCodeTypeValue, 
  areKeysValid, 
  getPercentageDiff, 
  generateVariationTooltip,
  formatRiskCell,
  ratingColor,
  copyToClipboard,
  isArrayEmpty,
  formatEventTypeLabel,
  getEventIcon,
  formatDate,
  getCodeTypesAndCodeValues,
  getEditCarrierCodeTypeAndValue,
  generateInvitationUrl,
  getCredentialStatuses,
  exportTableAsCSV,
  isShortcodeExcludedForDriversDashboardAccess,
  isShortcodeEnabledForUnderwritersScoreDashboard,
  isShortcodeEnabledForNBFleetDashboard,
  isShortcodeEnabledForNBWidgetFrenchStrings,
  isShortcodeExcludedForMileageLocationReporting,
  abbreviateThousandsNumber,
  formatDateToUTC,
  formatCompactNumber,
  formatDateMMDDYYYY,
  formatDateYYYYMMDD,
  getHostInfo,
  isObjectEmpty,
  formatNumber,
  formatKm,
  formatDateTimeNow,
  timeAgo,
}
